import React, { useState } from 'react';
import { parseModuleChecks, Check, valueLabel } from '../utils/parseModuleChecks';

interface Props {
  auditResult: any;
  modules: any[];
}

const statusLabel: Record<string, string> = {
  pass: 'PASS',
  fail: 'FAIL',
  warn: 'WARN',
  info: 'INFO',
  green: 'PASS',
  yellow: 'WARN',
  red: 'FAIL',
  blue: 'ERROR',
};

const readableStatusLabel: Record<string, string> = {
  pass: 'Compliant',
  fail: 'Non-Compliant',
  warn: 'Needs Attention',
  info: 'Informational',
  unassessed: 'Not assessed',
};

const severityLabel: Record<string, string> = {
  critical: 'CRITICAL',
  high: 'HIGH',
  medium: 'MEDIUM',
  low: 'LOW',
};

function formatDetailsAsEvidence(mod: any): string {
  const details = mod.details;
  if (!details || typeof details !== 'object') return 'No evidence data available.';

  const checks: Check[] = parseModuleChecks(details);
  if (checks.length === 0) return 'No individual checks recorded for this module.';

  const lines: string[] = [];
  lines.push(`Module: ${mod.name}`);
  lines.push(`Overall Result: ${readableStatusLabel[mod.status] || mod.status || 'Unknown'}`);
  lines.push(`Checks Evaluated: ${checks.length}`);
  lines.push('');
  lines.push('─'.repeat(60));

  const failChecks = checks.filter(c => c.status === 'fail');
  const warnChecks = checks.filter(c => c.status === 'warn');
  const passChecks = checks.filter(c => c.status === 'pass');
  const infoChecks = checks.filter(c => c.status === 'info');
  const unassessedChecks = checks.filter(c => c.status === 'unassessed');

  if (failChecks.length > 0) {
    lines.push('');
    lines.push(`NON-COMPLIANT FINDINGS (${failChecks.length}):`);
    lines.push('');
    for (const check of failChecks) {
      lines.push(`  [FAIL] ${check.name}${check.severity ? ` (${severityLabel[check.severity]})` : ''}`);
      if (check.value) lines.push(`         ${valueLabel(check)}: ${check.value}`);
      if (check.description) lines.push(`         Expected: ${check.description}`);
      lines.push('');
    }
  }

  if (warnChecks.length > 0) {
    lines.push(`ITEMS NEEDING ATTENTION (${warnChecks.length}):`);
    lines.push('');
    for (const check of warnChecks) {
      lines.push(`  [WARN] ${check.name}${check.severity ? ` (${severityLabel[check.severity]})` : ''}`);
      if (check.value) lines.push(`         ${valueLabel(check)}: ${check.value}`);
      if (check.description) lines.push(`         Expected: ${check.description}`);
      lines.push('');
    }
  }

  if (passChecks.length > 0) {
    lines.push(`COMPLIANT CHECKS (${passChecks.length}):`);
    lines.push('');
    for (const check of passChecks) {
      lines.push(`  [PASS] ${check.name}`);
      if (check.value) lines.push(`         ${valueLabel(check)}: ${check.value}`);
    }
    lines.push('');
  }

  if (unassessedChecks.length > 0) {
    lines.push(`NOT ASSESSED (${unassessedChecks.length}):`);
    lines.push('');
    for (const check of unassessedChecks) {
      lines.push(`  [ -- ] ${check.name}`);
      if (check.value) lines.push(`         ${valueLabel(check)}: ${check.value}`);
    }
    lines.push('');
  }

  if (infoChecks.length > 0) {
    lines.push(`INFORMATIONAL (${infoChecks.length}):`);
    lines.push('');
    for (const check of infoChecks) {
      lines.push(`  [INFO] ${check.name}`);
      if (check.value) lines.push(`         Value: ${check.value}`);
    }
    lines.push('');
  }

  if (Array.isArray(details.failures) && details.failures.length > 0) {
    lines.push('─'.repeat(60));
    lines.push('FAILURE DETAILS:');
    for (const f of details.failures) {
      lines.push(`  - ${typeof f === 'string' ? f : f.name || f.message || JSON.stringify(f)}`);
    }
    lines.push('');
  }

  if (Array.isArray(details.warnings) && details.warnings.length > 0) {
    lines.push('─'.repeat(60));
    lines.push('WARNING DETAILS:');
    for (const w of details.warnings) {
      lines.push(`  - ${typeof w === 'string' ? w : w.name || w.message || JSON.stringify(w)}`);
    }
    lines.push('');
  }

  return lines.join('\n');
}

const AuditTerminalView: React.FC<Props> = ({ auditResult, modules }) => {
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'evidence' | 'raw'>('evidence');

  if (!auditResult && modules.length === 0) {
    return (
      <div className="bg-black/40 border border-white/[0.06] rounded-xl p-8 text-center">
        <p className="text-white/30 text-sm font-mono">No audit output available</p>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a14] border border-white/[0.06] rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-white/[0.02] border-b border-white/[0.06]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        <span className="text-[10px] text-white/30 font-mono ml-2">
          audit evidence — {modules.length} {modules[0]?.metadata?.source === 'cis_scores' ? 'CIS controls' : 'modules'}
        </span>
        <div className="ml-auto flex gap-1">
          <button
            onClick={() => setViewMode('evidence')}
            className={`text-[10px] px-2 py-0.5 rounded font-mono transition-colors ${
              viewMode === 'evidence' ? 'bg-white/10 text-white/70' : 'text-white/25 hover:text-white/40'
            }`}
          >
            Evidence View
          </button>
          <button
            onClick={() => setViewMode('raw')}
            className={`text-[10px] px-2 py-0.5 rounded font-mono transition-colors ${
              viewMode === 'raw' ? 'bg-white/10 text-white/70' : 'text-white/25 hover:text-white/40'
            }`}
          >
            Raw Data
          </button>
        </div>
      </div>

      <div className="p-4 space-y-2 max-h-[600px] overflow-y-auto">
        {modules.map((mod) => {
          const isExpanded = expandedModule === mod.nodeId;
          const rawStdout = mod.metadata?.rawStdout || mod.metadata?.raw_stdout;
          const rawStderr = mod.metadata?.rawStderr || mod.metadata?.raw_stderr;
          const hasRaw = rawStdout || rawStderr;

          let displayOutput = '';
          if (viewMode === 'raw') {
            if (hasRaw) {
              displayOutput = rawStdout || '';
              if (rawStderr) displayOutput += '\n[STDERR]\n' + rawStderr;
            } else if (mod.details) {
              displayOutput = JSON.stringify(mod.details, null, 2);
            }
          } else {
            if (mod.metadata?.source === 'cis_scores' && mod.details) {
              const lines = [`Control: ${mod.name}`, `Score: ${mod.score ?? '—'}%`, `Status: ${mod.status}`, `Coverage: ${mod.details.coverage || '—'}`];
              if (mod.findings) lines.push(`Findings: ${mod.findings}`);
              displayOutput = lines.join('\n');
            } else {
              displayOutput = formatDetailsAsEvidence(mod);
            }
          }

          const normalizedStatus = (mod.status || '').toLowerCase();
          const statusColor = ['green', 'pass', 'success'].includes(normalizedStatus)
            ? 'text-green-400' : ['yellow', 'warn', 'warning'].includes(normalizedStatus)
            ? 'text-yellow-400' : ['red', 'fail', 'critical'].includes(normalizedStatus)
            ? 'text-red-400' : 'text-blue-400';

          const readableResult = readableStatusLabel[normalizedStatus] ||
            readableStatusLabel[statusLabel[normalizedStatus]?.toLowerCase()] || normalizedStatus;

          return (
            <div key={mod.nodeId}>
              <button
                onClick={() => setExpandedModule(isExpanded ? null : mod.nodeId)}
                className="w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/[0.03] transition-colors text-left"
              >
                <span className="text-white/20 text-xs font-mono">{isExpanded ? '▼' : '▶'}</span>
                <span className={`text-xs font-mono ${statusColor}`}>
                  [{statusLabel[normalizedStatus] || '????'}]
                </span>
                <span className="text-sm font-mono text-white/70">{mod.name}</span>
                <span className={`text-[10px] ml-auto ${statusColor}`}>{readableResult}</span>
              </button>

              {isExpanded && displayOutput && (
                <pre className={`mt-1 ml-6 p-3 bg-black/30 rounded text-[11px] font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-y-auto ${
                  viewMode === 'raw' ? 'text-green-300/70' : 'text-white/60'
                }`}>
                  {displayOutput}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AuditTerminalView;
